import { Component, Input, Output, EventEmitter, inject, computed, signal, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonContent, IonActionSheet, PopoverController } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { close, share, create, chatbubble, call, globe, location, time, business, checkmark, pencil, chevronUp, chevronDown, copy } from 'ionicons/icons';
import { ModalMaterialsSectionComponent } from '../modal-materials-section/modal-materials-section.component';
import { LeerMasComponent } from '../leer-mas/leer-mas.component';
import { HeaderModalComponent } from '../header-modal/header-modal.component';
import { RecyclingPoint } from '../../../types/recycling-point.interface';
import { CustomModalController } from '../../../shared/services/custom-modal-controller.service';
import { AuthService } from '../../../shared/services/auth.service';
import { PosthogService } from '../../../shared/services/posthog.service';
import { mapEventsState } from '../../../state/map-events.state';
import { SwNavigatorService } from '../../../shared/services/sw-navigator.service';
import { ScreenLayer } from '../../../shared/enums/screen-layer.enum';
import { PointDataService } from '../../../shared/services/point-data.service';
import { PopUpComponent } from '../pop-up/pop-up.component';
import type { PopupModel } from '../../../types/popup-model';
import { getAddressFromEntity } from '../../../shared/utils/location.utils';
import { ShareService } from '../../../shared/services/share.service';
import { SocialSectionComponent } from '../social-section/social-section.component';
import { ActionButtonsSliderComponent, ActionButton, MODAL_ACTION_BUTTONS } from '../action-buttons-slider/action-buttons-slider.component';
import { LinksChipsComponent } from '../links-chips/links-chips.component';
import { TrustSourceBadgeComponent } from '../trust-source-badge/trust-source-badge.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { TranslationService } from '../../../shared/services/translation.service';

export interface ModalAction {
  type: 'share' | 'comment' | 'edit' | 'save' | 'contact';
  data?: any;
}

@Component({
  selector: 'app-container-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonContent,
    IonActionSheet,
    ModalMaterialsSectionComponent,
    LeerMasComponent,
    HeaderModalComponent,
    SocialSectionComponent,
    ActionButtonsSliderComponent,
    LinksChipsComponent,
    TrustSourceBadgeComponent,
    TranslatePipe
  ],
  templateUrl: './container-modal.component.html',
  styleUrl: './container-modal.component.scss'
})
export class ContainerModalComponent implements OnChanges, OnInit {
  private modalCtrl = inject(CustomModalController);
  private swnav = inject(SwNavigatorService);
  private pointDataService = inject(PointDataService);
  private authService = inject(AuthService);
  private popoverCtrl = inject(PopoverController);
  private posthog = inject(PosthogService);
  private shareService = inject(ShareService);
  private tService = inject(TranslationService);

  // Internal signal for reactive computed properties
  private _container$ = signal<RecyclingPoint | null>(null);

  @Input() container: RecyclingPoint | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['container']) {
      this._container$.set(changes['container'].currentValue);
    }
  }

  @Input() initialHeight?: number;

  @Output() modalAction = new EventEmitter<ModalAction>();
  @Output() dismiss = new EventEmitter<void>();
  @Output() repointMap = new EventEmitter<{ lat: number; lng: number }>();

  // Input materials (what the container holds)
  readonly inputMaterials = computed(() => {
    const container = this._container$();
    if (!container) return [];
    if (container.materialInput && Array.isArray(container.materialInput) && container.materialInput.length > 0) {
      return container.materialInput;
    }
    if (container.materials && Array.isArray(container.materials) && container.materials.length > 0) {
      return container.materials;
    }
    return [];
  });

  // Output materials (what the container delivers)
  readonly outputMaterials = computed(() => {
    const container = this._container$();
    if (!container) return [];
    if (container.materialOutput && Array.isArray(container.materialOutput) && container.materialOutput.length > 0) {
      return container.materialOutput;
    }
    return [];
  });

  // Address string computed from container
  readonly addressString = computed(() => {
    const container = this._container$();
    return getAddressFromEntity(container, { excludeCities: [], fallback: this.tService.instant('containerModal.locationNotAvailable') });
  });

  // Show edit button when container is missing materials or address
  readonly showEditButton = computed(() => {
    const container = this._container$();
    if (!container) return false;
    const hasMaterials = (container.materialInput?.length || 0) > 0
      || (container.materials?.length || 0) > 0;
    const hasAddress = !!(container.address?.street || container.address?.fullAddress);
    return !hasMaterials || !hasAddress;
  });

  // Action buttons for slider
  actionButtons: ActionButton[] = [...MODAL_ACTION_BUTTONS];

  // Comment state
  showCommentSection: boolean = true;

  // Action sheet state
  isActionSheetOpen: boolean = false;
  actionSheetButtons = [
    {
      text: this.tService.instant('containerModal.shareLocation'),
      icon: 'location',
      handler: () => this.shareLocation()
    },
    {
      text: this.tService.instant('containerModal.shareInfo'),
      icon: 'business',
      handler: () => this.shareInfo()
    },
    {
      text: this.tService.instant('containerModal.copyAddress'),
      icon: 'copy',
      handler: () => this.copyAddress()
    },
    {
      text: this.tService.instant('common.cancel'),
      role: 'cancel'
    }
  ];

  constructor() {
    addIcons({
      close,
      share,
      create,
      chatbubble,
      call,
      globe,
      location,
      time,
      business,
      checkmark,
      pencil,
      chevronUp,
      chevronDown,
      copy
    });
    // Track modal open
    setTimeout(() => {
      this.posthog.init();
      this.posthog.capture('modal_open', { modal: 'container', containerId: this.container?.id });
    }, 0);
  }

  ngOnInit() {
    // Ensure signal is populated for dynamically created components (Ionic modals)
    // where componentProps might not trigger ngOnChanges on first render
    if (this.container && !this._container$()) {
      this._container$.set(this.container);
    }
  }

  // Modal actions
  closeModal() {
    this.posthog.capture('modal_close', { modal: 'container', containerId: this.container?.id });
    this.modalCtrl.dismiss(null, 'close');
  }

  toggleModalHeight() {
    this.posthog.capture('modal_toggle_height', { modal: 'container', containerId: this.container?.id });
    this.modalCtrl.toggleHeight();
  }

  repointMapOnContainer(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    if (!this.container?.position) return;
    
    console.log('🗺️ Repoint button clicked, requesting map repoint via global state');
    
    // Use global state instead of event emitter
    mapEventsState.requestRecenter({
      lat: this.container.position.lat,
      lng: this.container.position.lng
    });
    
    this.posthog.capture('repoint_map', { containerId: this.container?.id });
  }

  async suggestEdit() {
    if (!this.container) return;

    this.posthog.capture('suggest_edit', { containerId: this.container?.id });

    // Set point data in shared service for edit component
    this.pointDataService.setPointForEdit(this.container);

    // Navigate to edit page with explicit back path to home
    this.swnav.navigateObj({ path: `/edit-point/${this.container.id}`, back_path: '/home', layer: ScreenLayer.Edit, typeTransition: 1});
  }

  /**
   * Show popup to prompt user to login
   */
  private async showLoginPopup(): Promise<void> {
    const popupData: PopupModel = {
      titleh2: this.tService.instant('containerModal.loginTitle'),
      bodyText: this.tService.instant('containerModal.loginBody'),
      btn1: this.tService.instant('containerModal.loginBtn'),
      btn2: this.tService.instant('common.cancel'),
      btn1Style: 1,
      icon: 'person.svg',
      iconSize: 2,
      behavior: 2
    };

    const popover = await this.popoverCtrl.create({
      component: PopUpComponent,
      componentProps: {
        popupInfo: popupData
      },
      cssClass: 'custom-popover',
      backdropDismiss: false
    });

    await popover.present();

    const { data } = await popover.onDidDismiss();

    if (data === 1) {
      this.swnav.navigateObj({ path: '/profile', back_path: '/home', layer: ScreenLayer.Edit, closeModals: true });
    }
  }

  // Share functionality
  openShareOptions() {
    this.posthog.capture('open_share_options', { containerId: this.container?.id });
    this.isActionSheetOpen = true;
  }

  shareLocation() {
    if (!this.container) return;
    const entity = this.shareService.fromRecyclingPoint(this.container);
    this.shareService.shareLocation(entity);

    this.modalAction.emit({
      type: 'share',
      data: { type: 'location', container: this.container }
    });
    this.posthog.capture('share_location', { containerId: this.container?.id });
  }

  shareInfo() {
    if (!this.container) return;
    const entity = this.shareService.fromRecyclingPoint(this.container);
    this.shareService.shareInfo(entity);

    this.modalAction.emit({
      type: 'share',
      data: { type: 'info', container: this.container }
    });
    this.posthog.capture('share_info', { containerId: this.container?.id });
  }

  copyAddress() {
    if (!this.container) return;
    const entity = this.shareService.fromRecyclingPoint(this.container);
    this.shareService.copyToClipboard(this.shareService.getAddressString(entity));
    this.posthog.capture('copy_address', { containerId: this.container?.id });
  }

  // DEPRECATED: Use addressString() computed signal instead
  getAddressString(): string {
    return this.addressString();
  }

  // DEPRECATED: Use showEditButton() computed signal instead
  shouldShowEditButton(): boolean {
    return this.showEditButton();
  }

  formatDate(date: Date): string {
    if (!date) return '';
    
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return minutes <= 1 ? 'Hace un momento' : `Hace ${minutes} minutos`;
      }
      return hours === 1 ? 'Hace 1 hora' : `Hace ${hours} horas`;
    }
    if (days === 1) return 'Ayer';
    if (days < 7) return `Hace ${days} días`;
    
    return new Date(date).toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  // Search on Google
  searchContainerOnGoogle() {
    if (!this.container?.name) return;
    
    const query = encodeURIComponent(`${this.container.name} contenedor reciclaje`);
    window.open(`https://www.google.com/search?q=${query}`, '_blank');
    this.posthog.capture('search_google', { containerId: this.container?.id });
  }

  /** Handle action slider button clicks */
  onActionButtonClick(actionId: string) {
    switch (actionId) {
      case 'share':
        this.shareInfo();
        break;
      case 'google':
        this.searchContainerOnGoogle();
        break;
      case 'edit':
        this.suggestEdit();
        break;
      case 'report':
        this.scrollToReports();
        break;
      case 'save':
        this.toggleSave();
        break;
    }
  }

  /** Scroll to the social/report section and highlight it */
  private scrollToReports() {
    const el = document.querySelector('.status-report-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        el.classList.add('ui-guide');
        el.addEventListener('animationend', () => {
          el.classList.add('ui-guide-out');
          setTimeout(() => {
            el.classList.remove('ui-guide', 'ui-guide-out');
          }, 300);
        }, { once: true });
      }, 400);
    }
    this.posthog.capture('action_report_click', { containerId: this.container?.id });
  }

  /** Toggle save/bookmark */
  private toggleSave() {
    const saveBtn = this.actionButtons.find(b => b.id === 'save');
    if (saveBtn) {
      saveBtn.active = !saveBtn.active;
    }
    this.modalAction.emit({ type: 'save', data: { containerId: this.container?.id, saved: saveBtn?.active } });
    this.posthog.capture('action_save_toggle', { containerId: this.container?.id, saved: saveBtn?.active });
  }
}
